// Mobile-first responsive design utilities and breakpoint configurations
import { useTheme, useMediaQuery } from '@mui/material';

// Custom breakpoint values for better mobile responsiveness
export const breakpoints = {
  values: {
    xs: 0,      // Mobile phones (portrait)
    sm: 600,    // Mobile phones (landscape) / Small tablets
    md: 900,    // Tablets (portrait)
    lg: 1200,   // Tablets (landscape) / Small laptops
    xl: 1536,   // Desktop
    xxl: 1920   // Large desktop
  }
};

// Mobile-first responsive spacing scale
export const spacing = {
  xs: 4,   // 4px
  sm: 8,   // 8px
  md: 16,  // 16px
  lg: 24,  // 24px
  xl: 32,  // 32px
  xxl: 48  // 48px
};

// Typography responsive scaling
export const responsiveTypography = {
  h1: {
    fontSize: {
      xs: '1.75rem',  // 28px on mobile
      sm: '2rem',     // 32px on tablet
      md: '2.25rem',  // 36px on small desktop
      lg: '2.5rem',   // 40px on desktop
      xl: '3rem'      // 48px on large desktop
    },
    lineHeight: {
      xs: 1.3,
      sm: 1.25,
      md: 1.2
    }
  },
  h2: {
    fontSize: {
      xs: '1.5rem',   // 24px on mobile
      sm: '1.75rem',  // 28px on tablet
      md: '2rem',     // 32px on desktop
      lg: '2.25rem'   // 36px on large desktop
    },
    lineHeight: 1.3
  },
  h3: {
    fontSize: {
      xs: '1.25rem',  // 20px on mobile
      sm: '1.5rem',   // 24px on tablet
      md: '1.75rem',  // 28px on desktop
      lg: '2rem'      // 32px on large desktop
    },
    lineHeight: 1.35
  },
  h4: {
    fontSize: {
      xs: '1.125rem', // 18px on mobile
      sm: '1.25rem',  // 20px on tablet
      md: '1.5rem',   // 24px on desktop
    },
    lineHeight: 1.4
  },
  h5: {
    fontSize: {
      xs: '1rem',     // 16px on mobile
      sm: '1.125rem', // 18px on tablet
      md: '1.25rem',  // 20px on desktop
    },
    lineHeight: 1.4
  },
  h6: {
    fontSize: {
      xs: '0.875rem', // 14px on mobile
      sm: '1rem',     // 16px on tablet
      md: '1.125rem', // 18px on desktop
    },
    lineHeight: 1.5
  },
  body1: {
    fontSize: {
      xs: '0.875rem', // 14px on mobile
      sm: '0.9375rem',// 15px on tablet
      md: '1rem',     // 16px on desktop
    },
    lineHeight: 1.6
  },
  body2: {
    fontSize: {
      xs: '0.8125rem',// 13px on mobile
      sm: '0.875rem', // 14px on tablet
      md: '0.9375rem',// 15px on desktop
    },
    lineHeight: 1.6
  },
  caption: {
    fontSize: {
      xs: '0.6875rem',// 11px on mobile
      sm: '0.75rem',  // 12px on tablet
      md: '0.8125rem',// 13px on desktop
    },
    lineHeight: 1.5
  }
};

// Compact typography for space-efficient UI
export const compactTypography = {
  h1: {
    fontSize: {
      xs: '1.25rem',  // 20px on mobile  
      sm: '1.375rem', // 22px on tablet
      md: '1.5rem',   // 24px on desktop
      lg: '1.625rem', // 26px on large desktop
    },
    lineHeight: 1.2,
    fontWeight: 600
  },
  h2: {
    fontSize: {
      xs: '1.125rem', // 18px on mobile
      sm: '1.25rem',  // 20px on tablet
      md: '1.375rem', // 22px on desktop
    },
    lineHeight: 1.25,
    fontWeight: 600
  },
  h3: {
    fontSize: {
      xs: '1rem',     // 16px on mobile
      sm: '1.125rem', // 18px on tablet
      md: '1.25rem',  // 20px on desktop
    },
    lineHeight: 1.3,
    fontWeight: 600
  },
  h4: {
    fontSize: {
      xs: '0.875rem', // 14px on mobile
      sm: '0.9375rem',// 15px on tablet
      md: '1rem',     // 16px on desktop
    },
    lineHeight: 1.35,
    fontWeight: 600
  },
  h5: {
    fontSize: {
      xs: '0.8125rem',// 13px on mobile
      sm: '0.875rem', // 14px on tablet
      md: '0.9375rem',// 15px on desktop
    },
    lineHeight: 1.4,
    fontWeight: 600
  },
  h6: {
    fontSize: {
      xs: '0.75rem',  // 12px on mobile
      sm: '0.8125rem',// 13px on tablet
      md: '0.875rem', // 14px on desktop
    },
    lineHeight: 1.4,
    fontWeight: 600
  },
  subtitle1: {
    fontSize: {
      xs: '0.8125rem', // 13px on mobile
      sm: '0.875rem', // 14px on tablet
      md: '0.9375rem', // 15px on desktop
    },
    lineHeight: 1.4,
    fontWeight: 600,
  },
  subtitle2: {
    fontSize: {
      xs: '0.75rem', // 12px on mobile
      sm: '0.8125rem', // 13px on tablet
      md: '0.875rem', // 14px on desktop
    },
    lineHeight: 1.4,
    fontWeight: 600,
  },
  body1: {
    fontSize: {
      xs: '0.8125rem',// 13px on mobile
      sm: '0.875rem', // 14px on tablet
      md: '0.9375rem',// 15px on desktop
    },
    lineHeight: 1.5
  },
  body2: {
    fontSize: {
      xs: '0.75rem',  // 12px on mobile
      sm: '0.8125rem',// 13px on tablet
      md: '0.875rem', // 14px on desktop
    },
    lineHeight: 1.5
  },
  caption: {
    fontSize: {
      xs: '0.625rem', // 10px on mobile
      sm: '0.6875rem',// 11px on tablet
      md: '0.75rem',  // 12px on desktop
    },
    lineHeight: 1.4
  },
  button: {
    fontSize: {
      xs: '0.8125rem',// 13px on mobile
      sm: '0.875rem', // 14px on tablet
      md: '0.9375rem',// 15px on desktop
    },
    textTransform: 'none',
    fontWeight: 500
  },
  overline: {
    fontSize: {
      xs: '0.625rem', // 10px on mobile
      sm: '0.6875rem',// 11px on tablet
      md: '0.75rem',  // 12px on desktop
    },
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  }
};

// Extra-dense typography for EMR-style, data-heavy screens
export const emrTypography = {
  h1: {
    fontSize: {
      xs: '1.125rem', // 18px on mobile
      sm: '1.25rem', // 20px on tablet
      md: '1.375rem', // 22px on desktop
      lg: '1.5rem', // 24px on large desktop
    },
    lineHeight: 1.2,
    fontWeight: 700,
  },
  h2: {
    fontSize: {
      xs: '1rem', // 16px on mobile
      sm: '1.125rem', // 18px on tablet
      md: '1.25rem', // 20px on desktop
    },
    lineHeight: 1.25,
    fontWeight: 700,
  },
  h3: {
    fontSize: {
      xs: '0.9375rem', // 15px on mobile
      sm: '1rem', // 16px on tablet
      md: '1.125rem', // 18px on desktop
    },
    lineHeight: 1.3,
    fontWeight: 700,
  },
  h4: {
    fontSize: {
      xs: '0.875rem', // 14px on mobile
      sm: '0.9375rem', // 15px on tablet
      md: '1rem', // 16px on desktop
    },
    lineHeight: 1.35,
    fontWeight: 700,
  },
  h5: {
    fontSize: {
      xs: '0.8125rem', // 13px on mobile
      sm: '0.875rem', // 14px on tablet
      md: '0.9375rem', // 15px on desktop
    },
    lineHeight: 1.4,
    fontWeight: 700,
  },
  h6: {
    fontSize: {
      xs: '0.75rem', // 12px on mobile
      sm: '0.8125rem', // 13px on tablet
      md: '0.875rem', // 14px on desktop
    },
    lineHeight: 1.4,
    fontWeight: 700,
  },
  subtitle1: {
    fontSize: {
      xs: '0.8125rem', // 13px
      sm: '0.875rem', // 14px
      md: '0.9375rem', // 15px
    },
    lineHeight: 1.4,
    fontWeight: 600,
  },
  subtitle2: {
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.8125rem', // 13px
      md: '0.875rem', // 14px
    },
    lineHeight: 1.4,
    fontWeight: 600,
  },
  body1: {
    fontSize: {
      xs: '0.75rem', // 12px on mobile
      sm: '0.8125rem', // 13px on tablet
      md: '0.8125rem', // 13px on desktop
    },
    lineHeight: 1.45,
  },
  body2: {
    fontSize: {
      xs: '0.6875rem', // 11px on mobile
      sm: '0.75rem', // 12px on tablet
      md: '0.75rem', // 12px on desktop
    },
    lineHeight: 1.45,
  },
  caption: {
    fontSize: {
      xs: '0.625rem', // 10px on mobile
      sm: '0.6875rem', // 11px on tablet
      md: '0.6875rem', // 11px on desktop
    },
    lineHeight: 1.35,
  },
  button: {
    fontSize: {
      xs: '0.75rem', // 12px on mobile
      sm: '0.75rem', // 12px on tablet
      md: '0.8125rem', // 13px on desktop
    },
    textTransform: 'none',
    fontWeight: 600,
  },
  overline: {
    fontSize: {
      xs: '0.625rem', // 10px on mobile
      sm: '0.625rem', // 10px on tablet
      md: '0.6875rem', // 11px on desktop
    },
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
};

// Container max widths for different breakpoints
export const containerMaxWidths = {
  xs: '100%',
  sm: '100%',
  md: '900px',
  lg: '1200px',
  xl: '1400px',
  xxl: '1600px'
};

// Mobile-first padding and margin utilities
export const responsivePadding = {
  page: {
    xs: '16px',
    sm: '20px',
    md: '24px',
    lg: '32px',
    xl: '40px'
  },
  section: {
    xs: '12px',
    sm: '16px',
    md: '20px',
    lg: '24px',
    xl: '32px'
  },
  card: {
    xs: '12px',
    sm: '16px',
    md: '20px',
    lg: '24px'
  },
  button: {
    xs: '8px 12px',
    sm: '8px 16px',
    md: '10px 20px',
    lg: '12px 24px'
  }
};

// Grid responsive configurations
export const responsiveGrid = {
  gap: {
    xs: 1,    // 8px
    sm: 1.5,  // 12px
    md: 2,    // 16px
    lg: 2.5,  // 20px
    xl: 3     // 24px
  },
  columns: {
    xs: 4,    // Mobile: 4 columns
    sm: 6,    // Small tablet: 6 columns
    md: 8,    // Tablet: 8 columns
    lg: 12,   // Desktop: 12 columns
    xl: 12    // Large desktop: 12 columns
  }
};

// Touch-friendly sizes for mobile
export const touchTargets = {
  minimum: 44,      // Minimum touch target size (44x44px)
  comfortable: 48,  // Comfortable touch target size
  spacious: 56     // Spacious touch target size for primary actions
};

// Navigation responsive configurations
export const navigationSizes = {
  topBar: {
    height: {
      xs: 56,  // Mobile
      sm: 60,  // Tablet
      md: 64,  // Desktop
    }
  },
  sidebar: {
    width: {
      collapsed: 72,
      expanded: {
        xs: '100%',  // Full width on mobile
        sm: 280,     // Fixed width on tablet+
        md: 280,
        lg: 320
      }
    }
  },
  bottomNav: {
    height: 56,  // Fixed height for bottom navigation on mobile
    showBreakpoint: 'sm' // Show bottom nav below this breakpoint
  }
};

// Custom hooks for responsive design
export const useResponsive = () => {
  const theme = useTheme();
  
  return {
    isMobile: useMediaQuery(theme.breakpoints.down('sm')),
    isTablet: useMediaQuery(theme.breakpoints.between('sm', 'md')),
    isDesktop: useMediaQuery(theme.breakpoints.up('md')),
    isLargeDesktop: useMediaQuery(theme.breakpoints.up('xl')),
    
    // Orientation queries
    isPortrait: useMediaQuery('(orientation: portrait)'),
    isLandscape: useMediaQuery('(orientation: landscape)'),
    
    // Touch device detection
    isTouchDevice: useMediaQuery('(hover: none) and (pointer: coarse)'),
    
    // High resolution screens
    isRetina: useMediaQuery('(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)'),
    
    // Reduced motion preference
    prefersReducedMotion: useMediaQuery('(prefers-reduced-motion: reduce)'),
    
    // Color scheme preference
    prefersDarkMode: useMediaQuery('(prefers-color-scheme: dark)'),
    
    // Safe area insets for mobile devices with notches
    hasSafeAreaInsets: useMediaQuery('(display-mode: standalone)'),
  };
};

// Responsive sx prop utilities
export const responsiveSx = {
  // Responsive display utilities
  hideOnMobile: {
    display: { xs: 'none', sm: 'block' }
  },
  hideOnTablet: {
    display: { xs: 'block', sm: 'none', md: 'block' }
  },
  hideOnDesktop: {
    display: { xs: 'block', md: 'none' }
  },
  showOnlyOnMobile: {
    display: { xs: 'block', sm: 'none' }
  },
  showOnlyOnTablet: {
    display: { xs: 'none', sm: 'block', md: 'none' }
  },
  showOnlyOnDesktop: {
    display: { xs: 'none', md: 'block' }
  },
  
  // Responsive flex utilities
  flexColumn: {
    flexDirection: { xs: 'column', md: 'row' }
  },
  flexColumnReverse: {
    flexDirection: { xs: 'column-reverse', md: 'row' }
  },
  
  // Responsive text alignment
  textCenterMobile: {
    textAlign: { xs: 'center', md: 'left' }
  },
  
  // Responsive width utilities
  fullWidthMobile: {
    width: { xs: '100%', md: 'auto' }
  },
  
  // Safe area padding for mobile devices with notches
  safeAreaPadding: {
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  }
};

// Export all configurations
export default {
  breakpoints,
  spacing,
  responsiveTypography,
  compactTypography,
  emrTypography,
  containerMaxWidths,
  responsivePadding,
  responsiveGrid,
  touchTargets,
  navigationSizes,
  useResponsive,
  responsiveSx
};
